/**
 * PWA Background Sync Manager
 * 
 * Implements Background Sync API with comprehensive fallbacks
 * Handles mutations queue, retry logic, and optimistic updates
 */

import { DatabaseManager, PendingMutation } from './database';
import { decryptForTenant } from './encryption';
import {
  logger,
  captureMessage,
  buildTags,
  toSerializable,
  type SerializableObject,
} from '@/lib/monitoring/logger';

// PWA debug logging helper - only logs in development
const pwaLog = (message: string, data?: SerializableObject) => {
  if (process.env.NODE_ENV === 'development') {
    logger.info(`[PWA] ${message}`, data);
  }
};

type MutationType = PendingMutation['type'];
type MutationPriority = PendingMutation['priority'];

interface BaseMutationPayload {
  instrumentId: string;
  [key: string]: unknown;
}

interface AttachmentMutationPayload extends BaseMutationPayload {
  attachmentId: string;
  metadata?: unknown;
}

type MutationPayloadByType = {
  status_update: BaseMutationPayload;
  certificate_upload: BaseMutationPayload;
  attachment: AttachmentMutationPayload;
  usage_log: BaseMutationPayload & {
    endpoint: string;
    method?: 'POST' | 'PUT' | 'PATCH';
    query?: Record<string, string | null | undefined>;
    body: Record<string, unknown>;
  };
};

type MutationPayload<T extends MutationType = MutationType> = MutationPayloadByType[T];

type SyncEventType =
  | 'sync_start'
  | 'sync_complete'
  | 'sync_error'
  | 'mutation_processed'
  | 'mutation_queued';

interface SyncEventPayloadMap {
  sync_start: undefined;
  sync_complete: { success: boolean; processedCount: number; errorCount: number };
  sync_error: { error: unknown };
  mutation_processed: { mutationId: string; success: boolean; reason?: string };
  mutation_queued: {
    idempotencyKey: string;
    type: MutationType;
    priority: MutationPriority;
  };
}

type SyncEvent<T extends SyncEventType = SyncEventType> = {
  type: T;
  payload: SyncEventPayloadMap[T];
  timestamp: number;
};

type SyncEventHandler<T extends SyncEventType = SyncEventType> = (
  event: SyncEvent<T>
) => void;

type CrossTabMessage =
  | { type: 'leader_elected'; tabId: string; timestamp: number }
  | { type: 'sync_request'; sourceTabId: string; timestamp: number }
  | { type: 'tab_closed'; tabId: string; timestamp: number };

interface SyncStatistics {
  pendingMutations: number;
  pendingOptimisticUpdates: number;
  syncStatus: SyncStatus;
  isOnline: boolean;
  lastSyncAttempt: number;
}

interface EndpointDescriptor {
  url: string;
  method: string;
  body?: Record<string, unknown>;
}

// Sync configuration
export const SYNC_CONFIG = {
  batchSize: 10,                    // Mutations per batch
  batchSizeIOS: 5,                  // Smaller batches for iOS
  maxRetries: 3,                    // Max retry attempts
  baseDelay: 1000,                  // 1 second base delay
  maxDelay: 30000,                  // 30 seconds max delay
  jitterPercent: 0.1,               // 10% jitter
  syncTimeout: 30000,               // 30 seconds sync timeout
  fallbackInterval: 5 * 60 * 1000   // 5 minutes fallback interval
};

// Sync status types
export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

// Sync result
export interface SyncResult {
  success: boolean;
  processedCount: number;
  errorCount: number;
  duration: number;
  errors?: Error[];
}

// Optimistic update interface
interface OptimisticUpdate {
  mutationId: string;
  originalData: unknown;
  optimisticData: unknown;
  applied: boolean;
  confirmed: boolean;
  failed: boolean;
}

// Optimistic update manager
export class OptimisticUpdateManager {
  private updates = new Map<string, OptimisticUpdate>();

  /**
   * Apply optimistic update
   */
  applyOptimisticUpdate<TOriginal = unknown, TOptimistic = unknown>(
    mutationId: string,
    originalData: TOriginal,
    optimisticData: TOptimistic
  ): void {
    this.updates.set(mutationId, {
      mutationId,
      originalData,
      optimisticData,
      applied: true,
      confirmed: false,
      failed: false
    });
  }

  /**
   * Confirm update when sync succeeds
   */
  confirmUpdate(mutationId: string): void {
    const update = this.updates.get(mutationId);
    if (update) {
      update.confirmed = true;
      this.updates.delete(mutationId); // Remove confirmed update
    }
  }

  /**
   * Revert update when sync fails
   */
  revertUpdate(mutationId: string): unknown {
    const update = this.updates.get(mutationId);
    if (update) {
      update.failed = true;
      const originalData = update.originalData;
      this.updates.delete(mutationId);
      return originalData;
    }
    return null;
  }

  /**
   * Get pending optimistic updates
   */
  getPendingUpdates(): OptimisticUpdate[] {
    return Array.from(this.updates.values()).filter(u => !u.confirmed && !u.failed);
  }

  /**
   * Clear all updates
   */
  clear(): void {
    this.updates.clear();
  }
}

// Sync event listener
export class SyncEventManager {
  private listeners = new Map<SyncEventType, SyncEventHandler[]>();

  /**
   * Add event listener
   */
  addEventListener(eventType: SyncEventType, listener: SyncEventHandler): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType)!.push(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(eventType: SyncEventType, listener: SyncEventHandler): void {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * Emit sync event
   */
  emit<T extends SyncEventType>(eventType: T, payload: SyncEventPayloadMap[T]): void {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      const event: SyncEvent<T> = {
        type: eventType,
        payload,
        timestamp: Date.now()
      };

      listeners.forEach(listener => {
        try {
          (listener as SyncEventHandler<T>)(event);
        } catch (error) {
          logger.error(
            'Sync event listener error',
            error,
            toSerializable({ eventName: eventType })
          );
        }
      });
    }
  }
}

// Main background sync manager
export class BackgroundSyncManager {
  private dbManager: DatabaseManager;
  private optimisticUpdates = new OptimisticUpdateManager();
  private eventManager = new SyncEventManager();
  private syncStatus: SyncStatus = 'idle';
  private lastSyncAttemptAt = 0;
  private isIOSSafari: boolean;
  private fallbackTimers: Array<ReturnType<typeof setInterval>> = [];

  constructor(dbManager: DatabaseManager) {
    this.dbManager = dbManager;
    this.isIOSSafari = this.detectIOSSafari();
  }

  /**
   * Initialize background sync
   */
  async initialize(): Promise<void> {
    try {
      const hasBackgroundSync = await this.detectBackgroundSyncSupport();
      
      if (hasBackgroundSync) {
        pwaLog('BackgroundSync using Background Sync API');
        await this.setupBackgroundSync();
      } else {
        pwaLog('BackgroundSync using fallback strategies');
        this.setupFallbackStrategies();
      }

      pwaLog('BackgroundSync initialized successfully');
    } catch (error) {
      logger.error('BackgroundSync initialization failed', error);
      this.setupFallbackStrategies(); // Always have fallbacks
    }
  }

  /**
   * Detect Background Sync API support
   */
  private async detectBackgroundSyncSupport(): Promise<boolean> {
    try {
      if (!('serviceWorker' in navigator)) return false;
      
      const registration = await navigator.serviceWorker.ready;
      return 'sync' in registration;
    } catch (error) {
      logger.error('Background sync detection failed', error);
      return false;
    }
  }

  /**
   * Setup Background Sync API
   */
  private async setupBackgroundSync(): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.ready;
      
      if (registration.sync) {
        // Register sync event
        await registration.sync.register('sync-mutations');
        pwaLog('Background sync registered');
      }
    } catch (error) {
      logger.error('Background sync setup failed', error);
      throw error;
    }
  }

  /**
   * Setup fallback sync strategies
   */
  private setupFallbackStrategies(): void {
    // Online event listener
    window.addEventListener('online', () => {
      if (navigator.onLine) {
        pwaLog('Online event triggered sync');
        this.triggerSync();
      }
    });

    // Visibility change listener
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && navigator.onLine) {
        pwaLog('Visibility change triggered sync');
        this.triggerSync();
      }
    });

    // Focus event listener
    window.addEventListener('focus', () => {
      if (navigator.onLine) {
        pwaLog('Focus event triggered sync');
        this.triggerSync();
      }
    });

    // Periodic timer (only when online and visible)
    const periodicTimer = setInterval(() => {
      if (navigator.onLine && !document.hidden) {
        pwaLog('Periodic timer triggered sync');
        this.triggerSync();
      }
    }, SYNC_CONFIG.fallbackInterval);

    this.fallbackTimers.push(periodicTimer);
  }

  /**
   * Queue mutation for background sync
   */
  async queueMutation<T extends MutationType>(
    type: T,
    payload: MutationPayload<T>,
    priority: MutationPriority = 'medium',
    optimisticData?: MutationPayload<T>
  ): Promise<string> {
    try {
      // Generate idempotency key
      const idempotencyKey = crypto.randomUUID();

      // Apply optimistic update if provided
      if (optimisticData) {
        this.optimisticUpdates.applyOptimisticUpdate(
          idempotencyKey,
          payload, // original data
          optimisticData
        );
      }

      // Queue in database
      await this.dbManager.queueMutation(type, payload, priority, idempotencyKey);

      // Trigger immediate sync attempt if online
      if (navigator.onLine) {
        this.triggerSync();
      }

      this.eventManager.emit('mutation_queued', { idempotencyKey, type, priority });

      return idempotencyKey;
    } catch (error) {
      logger.error(
        'Failed to queue mutation for background sync',
        error,
        toSerializable({ type, priority })
      );
      throw error;
    }
  }

  /**
   * Trigger sync manually
   */
  async triggerSync(): Promise<SyncResult> {
    this.lastSyncAttemptAt = Date.now();
    if (this.syncStatus === 'syncing') {
      pwaLog('Sync already in progress');
      return { success: false, processedCount: 0, errorCount: 0, duration: 0 };
    }

    if (!navigator.onLine) {
      pwaLog('Offline, sync will happen when reconnected');
      this.syncStatus = 'offline';
      return { success: false, processedCount: 0, errorCount: 0, duration: 0 };
    }

    return this.performSync();
  }

  /**
   * Perform actual sync operation
   */
  private async performSync(): Promise<SyncResult> {
    const startTime = Date.now();
    this.syncStatus = 'syncing';
    this.eventManager.emit('sync_start', undefined);

    try {
      if (this.dbManager['currentTenantId'] && this.dbManager['currentUserId']) {
        this.dbManager.setContext(this.dbManager['currentTenantId'], this.dbManager['currentUserId']);
      }
      const pendingMutations = await this.dbManager.getPendingMutations();
      
      if (pendingMutations.length === 0) {
        this.syncStatus = 'idle';
        return { success: true, processedCount: 0, errorCount: 0, duration: 0 };
      }

      pwaLog('Syncing mutations', { count: pendingMutations.length });

      // Process in batches
      const batchSize = this.isIOSSafari ? SYNC_CONFIG.batchSizeIOS : SYNC_CONFIG.batchSize;
      let processedCount = 0;
      let errorCount = 0;
      const errors: Error[] = [];

      for (let i = 0; i < pendingMutations.length; i += batchSize) {
        const batch = pendingMutations.slice(i, i + batchSize);
        
        try {
          const batchResult = await this.processBatch(batch);
          processedCount += batchResult.processedCount;
          errorCount += batchResult.errorCount;
          
          if (batchResult.errors) {
            errors.push(...batchResult.errors);
          }
        } catch (error) {
          logger.error(
            'Background sync batch failed',
            error,
            toSerializable({ batchSize: batch.length })
          );
          errorCount += batch.length;
          errors.push(error as Error);
        }

        // Small delay between batches for iOS
        if (this.isIOSSafari && i + batchSize < pendingMutations.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      const duration = Date.now() - startTime;
      const success = errorCount === 0;

      this.syncStatus = success ? 'idle' : 'error';
      this.eventManager.emit('sync_complete', { success, processedCount, errorCount });

      return {
        success,
        processedCount,
        errorCount,
        duration,
        errors: errors.length > 0 ? errors : undefined
      };

    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('Background sync failed', error);
      
      this.syncStatus = 'error';
      this.eventManager.emit('sync_error', { error });

      return {
        success: false,
        processedCount: 0,
        errorCount: 1,
        duration,
        errors: [error as Error]
      };
    }
  }

  /**
   * Process batch of mutations
   */
  private async processBatch(mutations: PendingMutation[]): Promise<SyncResult> {
    let processedCount = 0;
    let errorCount = 0;
    const errors: Error[] = [];

    for (const mutation of mutations) {
      try {
        const success = await this.processMutation(mutation);
        
        if (success) {
          // Confirm optimistic update
          this.optimisticUpdates.confirmUpdate(mutation.idempotencyKey);
          
          // Remove from queue
          await this.dbManager.completeMutation(mutation.idempotencyKey);
          
          processedCount++;
          this.eventManager.emit('mutation_processed', { 
            mutationId: mutation.idempotencyKey,
            success: true
          });
        } else {
          // Handle retry or failure
          await this.handleMutationFailure(mutation);
          errorCount++;
        }
      } catch (error) {
        logger.error(
          'Mutation processing error',
          error,
          toSerializable({
            idempotencyKey: mutation.idempotencyKey,
            type: mutation.type,
          })
        );

        // Revert optimistic update
        this.optimisticUpdates.revertUpdate(mutation.idempotencyKey);

        await this.handleMutationFailure(mutation);
        errorCount++;
        errors.push(error as Error);
      }
    }

    return {
      success: errorCount === 0,
      processedCount,
      errorCount,
      duration: 0, // Will be calculated by caller
      errors: errors.length > 0 ? errors : undefined
    };
  }

  /**
   * Process individual mutation
   */
  private async processMutation(mutation: PendingMutation): Promise<boolean> {
    try {
      // Decrypt payload using active sync context, with auth fallback.
      const contextUserId =
        this.dbManager['currentUserId'] ??
        this.getCachedOperatorName() ??
        await this.getCurrentUserIdFromAuth();
      const userId = contextUserId ?? 'anonymous';
      const payload = await decryptForTenant(
        mutation.encryptedPayload,
        mutation.tenantId,
        userId
      );

      this.assertMutationPayload(mutation.type, payload);

      // Special handling for attachment uploads: stream file content from Cache Storage
      if (mutation.type === 'attachment') {
        try {
          const { attachmentManager } = await import('@/lib/offline/attachment-manager');
          const attachmentPayload = payload as MutationPayload<'attachment'>;

          // Retrieve decrypted file from local stores
          const result = await attachmentManager.getAttachment(attachmentPayload.attachmentId);
          if (!result.success || !result.file) {
            throw new Error(result.error || 'Attachment not available');
          }

          const form = new FormData();
          form.append('file', result.file, result.metadata?.originalFilename || `attachment-${Date.now()}`);
          if (result.metadata?.mimeType) form.append('mime_type', result.metadata.mimeType);
          if (result.metadata?.description) form.append('description', result.metadata.description);

          const response = await fetch(`/api/instruments/${attachmentPayload.instrumentId}/attachments`, {
            method: 'POST',
            headers: {
              'X-Idempotency-Key': mutation.idempotencyKey,
              'X-Background-Sync': '1'
            },
            body: form
          });

          if (response.ok) {
            // Cleanup local storage for this attachment
            await attachmentManager.cleanupStoredAttachment(attachmentPayload.attachmentId);
            pwaLog('Attachment synced successfully', { idempotencyKey: mutation.idempotencyKey });
            return true;
          }

          logger.error(
            'Attachment sync failed',
            new Error(`HTTP ${response.status}: ${response.statusText}`),
            toSerializable({
              idempotencyKey: mutation.idempotencyKey,
              status: response.status,
            })
          );
          return false;
        } catch (error) {
          logger.error(
            'Attachment processing error',
            error,
            toSerializable({ idempotencyKey: mutation.idempotencyKey })
          );
          return false;
        }
      }

      // Route to appropriate API endpoint based on mutation type (JSON mutations)
      const endpoint = this.getMutationEndpoint(mutation.type, payload);
      const response = await fetch(endpoint.url, {
        method: endpoint.method,
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': mutation.idempotencyKey
        },
        body: JSON.stringify(endpoint.body ?? payload)
      });

      if (response.ok) {
        pwaLog('Mutation synced successfully', { idempotencyKey: mutation.idempotencyKey });
        return true;
      } else {
        logger.error(
          'Mutation sync failed',
          new Error(`HTTP ${response.status}: ${response.statusText}`),
          toSerializable({
            idempotencyKey: mutation.idempotencyKey,
            status: response.status,
          })
        );
        return false;
      }
    } catch (error) {
      logger.error(
        'Mutation processing error in fetch',
        error,
        toSerializable({ idempotencyKey: mutation.idempotencyKey })
      );
      return false;
    }
  }

  /**
   * Get API endpoint for mutation type
   */
  private assertMutationPayload<T extends MutationType>(
    type: T,
    payload: unknown
  ): asserts payload is MutationPayload<T> {
    if (!payload || typeof payload !== 'object') {
      throw new Error(`Invalid payload for mutation type ${type}`);
    }

    const candidate = payload as {
      instrumentId?: unknown;
      attachmentId?: unknown;
    };

    if (typeof candidate.instrumentId !== 'string') {
      throw new Error(`Invalid payload for mutation type ${type}: missing instrumentId`);
    }

    if (type === 'attachment' && typeof candidate.attachmentId !== 'string') {
      throw new Error('Invalid attachment payload: missing attachmentId');
    }
  }

  /**
   * Get API endpoint for mutation type
   */
  private getMutationEndpoint<T extends MutationType>(
    type: T,
    payload: MutationPayload<T>
  ): EndpointDescriptor {
    switch (type) {
      case 'status_update':
        return {
          url: `/api/instruments/${payload.instrumentId}/status`,
          method: 'POST'
        };
      
      case 'certificate_upload':
        return {
          url: `/api/instruments/${payload.instrumentId}/certificate`,
          method: 'POST'
        };
      
      case 'attachment':
        return {
          url: `/api/instruments/${payload.instrumentId}/attachments`,
          method: 'POST'
        };

      case 'usage_log': {
        const usagePayload = payload as MutationPayloadByType['usage_log'];
        const params = new URLSearchParams();
        if (usagePayload.query) {
          for (const [key, value] of Object.entries(usagePayload.query)) {
            if (typeof value === 'string' && value.length > 0) {
              params.set(key, value);
            }
          }
        }
        const qs = params.toString();
        return {
          url: `${usagePayload.endpoint}${qs ? `?${qs}` : ''}`,
          method: usagePayload.method ?? 'POST',
          body: usagePayload.body,
        };
      }
      
      default:
        throw new Error(`Unknown mutation type: ${type}`);
    }
  }

  /**
   * Handle mutation failure with retry logic
   */
  private async handleMutationFailure(mutation: PendingMutation): Promise<void> {
    if (mutation.retries >= mutation.maxRetries) {
      void captureMessage('Mutation exceeded max retries, removing', 'warning', {
        tags: buildTags('pwa-sync', 'mutation-failure'),
        extra: toSerializable({
          idempotencyKey: mutation.idempotencyKey,
          type: mutation.type,
          retries: mutation.retries,
        }),
      });

      // Remove failed mutation
      await this.dbManager.completeMutation(mutation.idempotencyKey);

      // Revert optimistic update
      this.optimisticUpdates.revertUpdate(mutation.idempotencyKey);

      this.eventManager.emit('mutation_processed', {
        mutationId: mutation.idempotencyKey,
        success: false,
        reason: 'max_retries_exceeded'
      });
    } else {
      // Increment retry count with exponential backoff
      await this.dbManager.retryMutation(mutation.idempotencyKey);

      pwaLog('Mutation scheduled for retry', { idempotencyKey: mutation.idempotencyKey, retryCount: mutation.retries + 1 });
    }
  }

  /**
   * Check if there are pending mutations
   */
  async hasPendingMutations(): Promise<boolean> {
    try {
      const pending = await this.dbManager.getPendingMutations();
      return pending.length > 0;
    } catch (error) {
      logger.error('Failed to check pending mutations', error);
      return false;
    }
  }

  /**
   * Get sync status
   */
  getSyncStatus(): SyncStatus {
    if (!navigator.onLine) return 'offline';
    return this.syncStatus;
  }

  /**
   * Get sync statistics
   */
  async getSyncStats(): Promise<SyncStatistics> {
    try {
      const tenantId = this.dbManager['currentTenantId'];
      const userId = this.dbManager['currentUserId'];

      if (!tenantId || !userId) {
        pwaLog('BackgroundSync skipping stats fetch: tenant context not ready');
        return {
          pendingMutations: 0,
          pendingOptimisticUpdates: 0,
          syncStatus: this.syncStatus,
          isOnline: navigator.onLine,
          lastSyncAttempt: 0
        };
      }

      this.dbManager.setContext(tenantId, userId);
      const pendingMutations = await this.dbManager.getPendingMutations();
      const pendingUpdates = this.optimisticUpdates.getPendingUpdates();

      return {
        pendingMutations: pendingMutations.length,
        pendingOptimisticUpdates: pendingUpdates.length,
        syncStatus: this.syncStatus,
        isOnline: navigator.onLine,
        lastSyncAttempt: this.lastSyncAttemptAt
      };
    } catch (error) {
      logger.error('Failed to get sync stats', error);
      return {
        pendingMutations: 0,
        pendingOptimisticUpdates: 0,
        syncStatus: this.syncStatus,
        isOnline: navigator.onLine,
        lastSyncAttempt: 0
      };
    }
  }

  /**
   * Add sync event listener
   */
  onSyncEvent(eventType: SyncEventType, listener: SyncEventHandler): void {
    this.eventManager.addEventListener(eventType, listener);
  }

  /**
   * Remove sync event listener
   */
  offSyncEvent(eventType: SyncEventType, listener: SyncEventHandler): void {
    this.eventManager.removeEventListener(eventType, listener);
  }

  private getCachedOperatorName(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      const raw = window.localStorage.getItem('scopax:sticky-job');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { operatorName?: unknown };
      if (typeof parsed.operatorName !== 'string') return null;
      const value = parsed.operatorName.trim();
      return value.length > 0 ? value : null;
    } catch {
      return null;
    }
  }

  private async getCurrentUserIdFromAuth(): Promise<string | null> {
    try {
      const { createBrowserClient } = await import('@/lib/supabase/client');
      const client = createBrowserClient();
      const { data: { user } } = await client.auth.getUser();
      return user?.id ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Detect iOS Safari
   */
  private detectIOSSafari(): boolean {
    if (typeof navigator === 'undefined') return false;
    
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && 
           /Safari/.test(navigator.userAgent) && 
           !/CriOS|FxiOS/.test(navigator.userAgent);
  }

  /**
   * Dispose resources
   */
  setTenantContext(tenantId: string, userId: string): void {
    this.dbManager.setContext(tenantId, userId);
  }

  /**
   * Provide current tenant so worker scheduling can scope sync
   */
  getActiveTenantId(): string | null {
    return this.dbManager.getActiveTenantId();
  }

  dispose(): void {
    // Clear fallback timers
    this.fallbackTimers.forEach(timer => clearInterval(timer));
    this.fallbackTimers = [];

    // Clear optimistic updates
    this.optimisticUpdates.clear();

    // Reset status
    this.syncStatus = 'idle';
  }
}

// Cross-tab sync coordinator
export class CrossTabSyncCoordinator {
  private channel: BroadcastChannel | null = null;
  private isLeader = false;
  private leaderElectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly tabId: string;

  constructor() {
    this.tabId = this.generateTabId();
    if (typeof window === 'undefined') {
      return;
    }
    this.setupCrossTabCommunication();
  }

  /**
   * Setup cross-tab communication
   */
  private setupCrossTabCommunication(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if ('BroadcastChannel' in window) {
        this.channel = new BroadcastChannel('scopax_sync_coordination');

        this.channel.addEventListener('message', (event: MessageEvent) => {
          if (this.isCrossTabMessage(event.data)) {
            this.handleCrossTabMessage(event.data);
          }
        });

        // Start leader election
        this.startLeaderElection();
      }
    } catch (error) {
      logger.error('CrossTabSync setup failed', error);
    }
  }

  /**
   * Start leader election process
   */
  private startLeaderElection(): void {
    // Random delay to avoid conflicts
    const delay = Math.random() * 1000;
    
    this.leaderElectionTimeout = setTimeout(() => {
      this.claimLeadership();
    }, delay);
  }

  /**
   * Claim leadership for sync coordination
   */
  private claimLeadership(): void {
    this.isLeader = true;
    pwaLog('CrossTabSync claimed sync leadership', { tabId: this.tabId });

    if (this.channel) {
      this.channel.postMessage({
        type: 'leader_elected',
        tabId: this.tabId,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Handle cross-tab messages
   */
  private handleCrossTabMessage(message: CrossTabMessage): void {
    switch (message.type) {
      case 'leader_elected':
        if (!this.isLeader) {
          pwaLog('CrossTabSync: Another tab claimed leadership', { leaderTabId: message.tabId });
        }
        break;

      case 'sync_request':
        if (this.isLeader) {
          pwaLog('CrossTabSync: Received sync request from another tab', {
            requesterTabId: message.sourceTabId,
          });
          // Coordinate sync across tabs
        }
        break;
      
      case 'tab_closed':
        if (message.tabId === this.tabId) {
          // Another leader closed, start new election
          this.startLeaderElection();
        }
        break;
    }
  }

  /**
   * Type guard for cross-tab messages
   */
  private isCrossTabMessage(value: unknown): value is CrossTabMessage {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const candidate = value as { type?: unknown };
    if (candidate.type === 'leader_elected' || candidate.type === 'tab_closed') {
      return typeof (value as { tabId?: unknown }).tabId === 'string';
    }

    if (candidate.type === 'sync_request') {
      return typeof (value as { sourceTabId?: unknown }).sourceTabId === 'string';
    }

    return false;
  }

  /**
   * Generate unique tab ID
   */
  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Check if this tab is the sync leader
   */
  isLeaderTab(): boolean {
    return this.isLeader;
  }

  /**
   * Dispose resources
   */
  dispose(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    if (this.leaderElectionTimeout) {
      clearTimeout(this.leaderElectionTimeout);
      this.leaderElectionTimeout = null;
    }

    this.isLeader = false;
  }
}

// Global instances
export const backgroundSyncManager = new BackgroundSyncManager(new DatabaseManager());
export const crossTabCoordinator = new CrossTabSyncCoordinator();

// Utility functions
export async function initializeBackgroundSync(): Promise<BackgroundSyncManager> {
  await backgroundSyncManager.initialize();
  return backgroundSyncManager;
}

export async function queueMutationForSync<T extends MutationType>(
  type: T,
  payload: MutationPayload<T>,
  priority: MutationPriority = 'medium',
  optimisticData?: MutationPayload<T>
): Promise<string> {
  const mutationId = await backgroundSyncManager.queueMutation(type, payload, priority, optimisticData);
  const tenantContext = backgroundSyncManager.getActiveTenantId() ?? 'global';
  void scheduleWorkerSync(tenantContext);
  return mutationId;
}

export async function triggerManualSync(): Promise<SyncResult> {
  return backgroundSyncManager.triggerSync();
}

export async function getSyncStatus(): Promise<SyncStatistics> {
  try {
    return await backgroundSyncManager.getSyncStats();
  } catch (error) {
    pwaLog('getSyncStatus using fallback', { error: error instanceof Error ? error.message : String(error) });

    const isBrowserOnline = typeof navigator !== 'undefined' ? navigator.onLine : false;

    return {
      pendingMutations: 0,
      pendingOptimisticUpdates: 0,
      syncStatus: isBrowserOnline ? 'idle' : 'offline',
      isOnline: isBrowserOnline,
      lastSyncAttempt: Date.now(),
    };
  }
}

const BACKGROUND_SYNC_TAG_PREFIX = 'scopax-sync-';

async function scheduleWorkerSync(tenantId: string): Promise<void> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    registration.active?.postMessage({
      action: 'REGISTER_BACKGROUND_SYNC',
      payload: { tenantId }
    });

    if (registration.sync) {
      const tag = `${BACKGROUND_SYNC_TAG_PREFIX}${tenantId}`;
      await registration.sync.register(tag);
      captureMessage('pwa-sync.background-sync-registered', 'info', {
        tags: buildTags('pwa-sync', 'worker'),
        extra: { tenantId, tag }
      });
    } else {
      captureMessage('pwa-sync.background-sync-unavailable', 'info', {
        tags: buildTags('pwa-sync', 'worker'),
        extra: { tenantId }
      });
    }
  } catch (error) {
    captureMessage('pwa-sync.background-sync-register-failed', 'warning', {
      tags: buildTags('pwa-sync', 'worker'),
      extra: {
        tenantId,
        error: error instanceof Error ? error.message : String(error)
      }
    });
  }
}
